import axios from 'axios';
import Component from 'vue-class-component';
import { Inject, Vue, Watch } from 'vue-property-decorator';
import AccountService from '@/account/account.service';
import ConnectomeService from '@/entities/connectome/connectome.service';
import AskGooverService from '@/service/ask-goover.service';
import PublicService from '@/service/public.service';
import { namespace } from 'vuex-class';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';
import RegisterService from '@/account/register/register.service';
import { getCurrentTimeZoneOffset, LOCAL_STORAGE_CONSTANTS } from '@/shared/constants/ds-constants';
import PhoneService from '@/account/phone.service';
import TermsOfUse from '@/account/register/terms-of-use/terms-of-use.vue';
import PrivacyPolicy from '@/account/register/privacy-policy/privacy-policy.vue';
import { hideElementById } from '@/util/style-util';
import { MergeChatObject } from '@/shared/model/ask-goover.model';
import { USER_KEY_HEADER } from '@/shared/security/authority';
import { COUNTRIES } from '@/constants';

const appStoreState = namespace('appStore');
const navbarStore = namespace('navbarStore');

const validations = {
  formModel: {
    phoneNumber: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(50),
    },
    code: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(50),
    },
  },
};

@Component({
  components: {
    'terms-of-use': TermsOfUse,
    'privacy-policy': PrivacyPolicy,
  },
  validations,
})
export default class LoginForm extends Vue {
  @Inject('accountService')
  private accountService: () => AccountService;

  @Inject('connectomeService')
  private connectomeService: () => ConnectomeService;

  @Inject('publicService')
  private publicService: () => PublicService;

  @Inject('registerService') private registerService: () => RegisterService;

  // @Inject('phoneService') private phoneService: () => PhoneService;

  @Inject('askGooverService')
  private askGooverService: () => AskGooverService;

  @appStoreState.Action
  public changePage!: (payload: { isLoginPage: boolean }) => void;

  // @navbarStore.Action
  // public setResetCollection!: (payload: { newVal: boolean }) => void;

  @appStoreState.Getter
  public getIsShowPopupLogin!: boolean;

  @appStoreState.Mutation
  public setIsShowPopupRegister!: (payload: boolean) => void;

  @appStoreState.Mutation
  public setIsShowPopupLogin!: (payload: boolean) => void;

  public authenticationError = null;
  public jwt = null;
  public rememberMe = true;

  public isValid = false;
  public mailOrPhoneTxt = '';
  public isPhone = false;
  public isInvalidMailOrPhone = false;
  public hideVerifyButton = false;
  public minLeft: any = '03';
  public secLeft: any = '00';
  SEND_CODE_TIMEOUT = 180;
  public countDown;
  public isSendingCode = false;
  public confirmCode = '';
  public isExpiredCode = false;
  public isVerificationCodeSuccess = false;
  public existedMailOrPhone = true;
  sendedVerificationCode = false;
  isType = false;
  isShowResendCode = false;
  isFromPopupLogin = false;
  waitingLoginApi = false;
  public countrySelect = null;
  public countries = COUNTRIES;

  // Test phone number: 5571981265131
  public formModel = {
    countryCode: '',
    phoneNumber: '',
    type: 1,
    code: '',
    email: '',
    emailCode: '',
    login: '',
    timeZone: '',
  };

  mounted() {
    this.setLocaleByIp();
  }

  public doLogin(loginName, code) {
    const language = this.$i18n.locale;
    const data = {
      username: loginName,
      password: code,
      rememberMe: this.rememberMe,
      language,
      deviceId: '',
      countryCode: this.countrySelect,
    };
    // remove Guest token
    axios.get('api/removeToken').then();
    axios
      .post('api/authenticate', data)
      .then(result => {
        const bearerToken = result.headers.authorization;
        const tempUserKey = result.headers[USER_KEY_HEADER];

        if (bearerToken && bearerToken.slice(0, 7) === 'Bearer ') {
          this.jwt = bearerToken.slice(7, bearerToken.length);
          if (this.jwt != null) {
            if (this.rememberMe) {
              localStorage.setItem('ds-authenticationToken', this.jwt);
              sessionStorage.removeItem('ds-authenticationToken');
            } else {
              sessionStorage.setItem('ds-authenticationToken', this.jwt);
              localStorage.removeItem('ds-authenticationToken');
            }
          }
          this.processAfterLoginSuccess(loginName, tempUserKey);
        }
        this.authenticationError = false;
      })
      .catch(() => {
        this.authenticationError = true;
        this.isValid = true;
      });
  }

  setLocaleByIp() {
    const locale = localStorage.getItem(LOCAL_STORAGE_CONSTANTS.LOCALE);
    if (locale) {
      this.countrySelect = locale;
    } else {
      this.publicService()
        .getIpFromClient()
        .then((ip: string) => {
          this.publicService()
            .getLocationByIp(ip)
            .then(res => {
              this.countrySelect = this.countries.find(item => item.value == res?.data?.countryCode)?.value;
              this.setLocaleToCache(this.countrySelect);
            });
        });
    }
  }

  setLocaleToCache(locale) {
    if (!locale) {
      return;
    }
    localStorage.setItem(LOCAL_STORAGE_CONSTANTS.LOCALE, locale);
  }

  public processAfterLoginSuccess(login: string, userKeyInput?: string) {
    this.authenticationError = false;
    const conversationIdInput = localStorage.getItem('conversationId') || '';

    try {
      this.connectomeService()
        .getConnectomeByLoginOrPhone(login)
        .then(res => {
          if (res.data.length > 0) {
            this.accountService()
              .retrieveAccount()
              .then(async () => {
                this.isValid = true;

                if (this.isFromPopupLogin) {
                  // @ts-ignore
                  const tempUserId = userKeyInput || JSON.stringify(res?.data[0])?.user.id || '';

                  await this.mergeChatHistoryFromGuestToLoginUser(tempUserId, conversationIdInput);
                } else {
                  setTimeout(() => {
                    if (this.$route.name !== 'Home') {
                      this.$router.push('/home');
                    }
                    location.reload();
                  }, 200);
                }
              })
              .finally(() => {
                this.waitingLoginApi = false;
              });
          } else {
            this.accountService()
              .retrieveAccount()
              .then(async () => {
                this.isValid = true;
                if (this.isFromPopupLogin) {
                  const tempUserId = userKeyInput || '';

                  await this.mergeChatHistoryFromGuestToLoginUser(tempUserId, conversationIdInput);
                } else {
                  setTimeout(() => {
                    if (this.$route.name !== 'Home') {
                      this.$router.push('/home');
                    }
                    location.reload();
                  }, 200);
                }
              })
              .finally(() => {
                this.waitingLoginApi = false;
              });
          }
        });
    } catch (e) {
      this.accountService()
        .retrieveAccount()
        .then(async () => {
          this.isValid = true;
          if (this.isFromPopupLogin) {
            const tempUserId = userKeyInput || '';

            await this.mergeChatHistoryFromGuestToLoginUser(tempUserId, conversationIdInput);
          } else {
            setTimeout(() => {
              if (this.$route.name !== 'Home') {
                this.$router.push('/home');
              }
              location.reload();
            }, 200);
          }
        })
        .finally(() => {
          this.waitingLoginApi = false;
        });
    }
  }

  checkRegex() {
    this.isType = true;
    this.existedMailOrPhone = true;
    this.isInvalidMailOrPhone = false;
    this.isSendingCode = false;
    const patternPhone = new RegExp('\\b\\d{5,15}\\b');
    const patternEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (patternEmail.test(this.mailOrPhoneTxt.replace(/\.+(?=@)/g, ''))) {
      //is Email
      this.isPhone = false;
      this.isInvalidMailOrPhone = false;
    } else if (patternPhone.test(this.mailOrPhoneTxt)) {
      this.isPhone = true;
      this.isInvalidMailOrPhone = false;
    } else {
      this.isPhone = false;
      this.isInvalidMailOrPhone = true;
    }
  }

  resendCode() {
    this.isSendingCode = false;
    this.isVerificationCodeSuccess = false;
    this.sendedVerificationCode = false;
    this.resetTime();
    this.getVerificationCode();
  }

  getVerificationCode() {
    this.isType = true;

    this.mailOrPhoneTxt = this.mailOrPhoneTxt.trim().replace(' ', '').replace(/\s+/g, '');

    // handle no text and enter
    if (
      !this.mailOrPhoneTxt ||
      (this.mailOrPhoneTxt && this.mailOrPhoneTxt.length < 5) ||
      this.isInvalidMailOrPhone ||
      this.isSendingCode ||
      !this.existedMailOrPhone
    ) {
      return;
    }

    this.confirmCode = '';
    this.isExpiredCode = false;
    if (!(this.minLeft === '00' && this.secLeft === '00')) {
      this.resetTime();
      clearInterval(this.timeDown);
    }
    this.formModel.login = this.mailOrPhoneTxt;
    if (this.isPhone) {
      this.formModel.phoneNumber = this.mailOrPhoneTxt;
      this.formModel.countryCode = this.countrySelect;
    } else {
      this.formModel.email = this.mailOrPhoneTxt;
    }
    this.hideVerifyButton = true;
    this.sendOTP();

    // } else {
    //   this.callApiSendCodeToEmail();
    //   this.hideVerifyButton = true;
    // }
  }

  // callApiSendCodeToEmail() {
  //   if (!(this.minLeft === '00' && this.secLeft === '00')) {
  //     this.resetTime();
  //     clearInterval(this.timeDown);
  //   }
  //
  //   this.formModel.email = this.mailOrPhoneTxt;
  //   this.registerService()
  //     .sendEmailService(this.formModel)
  //     .then(res => {
  //       this.isSendingCode = true;
  //       this.existedMailOrPhone = true;
  //       this.formModel.login = this.mailOrPhoneTxt;
  //       this.formModel.login = res.data.login;
  //
  //       this.$nextTick(() => {
  //         // @ts-ignore
  //         this.$refs['verifyCode'].focus();
  //       });
  //
  //       this.isSendingCode = true;
  //       this.isShowResendCode = true;
  //     })
  //     .catch(error => {
  //       this.isShowResendCode = false;
  //       const err = error.response.data;
  //       switch (err.code) {
  //         case '010': {
  //           this.existedMailOrPhone = false;
  //           break;
  //         }
  //         case '011': {
  //           this.existedMailOrPhone = false;
  //           break;
  //         }
  //         default: {
  //           this.$bvToast.toast(`${this.$t('newLearningCenter.unknownError')}`, {
  //             toaster: 'b-toaster-bottom-right',
  //             title: `${this.$t('newLearningCenter.error')}`,
  //             variant: 'danger',
  //             solid: true,
  //             autoHideDelay: 4000,
  //           });
  //           break;
  //         }
  //       }
  //     });
  // }

  sendOTP() {
    this.registerService()
      .sendOTP(this.formModel)
      .then(res => {
        this.existedMailOrPhone = true;
        this.formModel.login = this.mailOrPhoneTxt;

        this.$nextTick(() => {
          // @ts-ignore
          this.$refs['verifyCode']?.focus();
        });

        this.isSendingCode = true;
        this.isShowResendCode = true;
      })
      .catch(error => {
        this.isShowResendCode = false;
        const err = error.response.data;
        switch (err.code) {
          case '010': {
            this.existedMailOrPhone = false;
            break;
          }
          case '011': {
            this.existedMailOrPhone = false;
            break;
          }
          case '003': {
            this.existedMailOrPhone = false;
            break;
          }
          case '004': {
            this.existedMailOrPhone = false;
            break;
          }
          default: {
            this.existedMailOrPhone = false;
            break;
          }
        }
      });
  }

  public confirm(isFromPopupLogin?: boolean) {
    if (isFromPopupLogin !== undefined && isFromPopupLogin === true) {
      this.isFromPopupLogin = true;
    } else {
      this.isFromPopupLogin = false;
    }

    if (!this.confirmCode) {
      return;
    }

    this.confirmCode = this.confirmCode.trim().replace(' ', '').replace(/\s+/g, '');

    if (!this.confirmCode || (this.confirmCode && this.confirmCode.length !== 6)) {
      return;
    }

    if (!this.checkRegexCode()) {
      return;
    }

    if (!this.isVerificationCodeSuccess && this.sendedVerificationCode) {
      return;
    }

    if (this.minLeft === '00' && this.secLeft === '00') {
      return;
    }

    if (this.minLeft == 0 && this.secLeft == 0 && this.sendedVerificationCode) {
      this.isExpiredCode = true;
      this.sendedVerificationCode = false;
      return;
    }
    if (this.mailOrPhoneTxt !== this.formModel.email && this.mailOrPhoneTxt !== this.formModel.phoneNumber) {
      this.resetTime();
      clearInterval(this.timeDown);
      return;
    }

    this.formModel.timeZone = getCurrentTimeZoneOffset();

    if (this.waitingLoginApi) {
      return;
    }

    this.waitingLoginApi = true;
    // if (this.isPhone) {
    this.formModel.code = this.confirmCode;
    this.verifyEmailCode();
    // } else {
    // this.formModel.emailCode = this.confirmCode;
    // this.verifyEmailCode();
    // }
  }

  // verifyPhoneCode() {
  //   this.formModel.code = this.confirmCode;
  //   this.phoneService()
  //     .verify(this.formModel)
  //     .then(res => {
  //       this.sendedVerificationCode = true;
  //       this.callback(res?.data?.feild || this.formModel.login, this.formModel.code);
  //       if (res?.data?.code == '000') {
  //         this.isVerificationCodeSuccess = true;
  //       }
  //       clearTimeout(this.countDown);
  //       this.$emit('update:loginFailedAttempt', 0);
  //     })
  //     .catch(error => {
  //       this.waitingLoginApi = false;
  //       this.sendedVerificationCode = true;
  //       const err = error.response.data;
  //       this.isVerificationCodeSuccess = false;
  //       this.$emit('update:loginFailedAttempt', err.loginFailedCount);
  //     });
  // }

  verifyEmailCode() {
    this.registerService()
      .login(this.formModel)
      .then(res => {
        this.sendedVerificationCode = true;
        this.callback(this.formModel.login, this.confirmCode);
        if (res?.data?.code == '000') {
          this.isVerificationCodeSuccess = true;
        }
        clearTimeout(this.countDown);
        this.$emit('update:loginFailedAttempt', 0);
      })
      .catch(error => {
        this.waitingLoginApi = false;
        this.sendedVerificationCode = true;
        const err = error.response.data;
        this.$emit('update:loginFailedAttempt', err.loginFailedCount);
        this.isVerificationCodeSuccess = false;
      });
  }

  timeDown;

  resetTime() {
    this.minLeft = '03';
    this.secLeft = '00';
  }

  public callback(loginName, code) {
    this.doLogin(loginName, code);
  }

  routeToRegister(navigate, e) {
    this.$router.push('/register');
  }

  @Watch('confirmCode')
  onConfirmCodeChange(value) {
    this.sendedVerificationCode = false;
  }

  checkRegexCode() {
    this.confirmCode = this.confirmCode.trim().replace(' ', '').replace(/\s+/g, '');

    if (!this.confirmCode || (this.confirmCode && this.confirmCode.length === 0)) {
      return false;
    }

    const numberRegex = /^\d+$/;
    return numberRegex.test(this.confirmCode);
  }

  finishCountdown() {
    this.minLeft = '00';
    this.secLeft = '00';
    this.isExpiredCode = true;
  }

  handleOpenPopupRegister() {
    this.handleClosePopupLogin();

    this.setIsShowPopupRegister(true);
  }

  handleClosePopupLogin() {
    this.setIsShowPopupLogin(false);

    hideElementById('buttonClosePopupLogin', true);
  }

  async mergeChatHistoryFromGuestToLoginUser(userIdGet?: string, conversationIdInput?: string) {
    if (userIdGet?.trim()?.length > 0 && conversationIdInput?.trim()?.length > 0 && !conversationIdInput?.includes('refused')) {
      const tempInput: MergeChatObject = {
        requestId: 'copy_chat_' + new Date().getTime(),
        conversationId: conversationIdInput,
        userKey: userIdGet,
      };

      this.askGooverService()
        .mergeChat(tempInput)
        .then(res => {
          const newConversationId = res.data;
          localStorage.setItem('conversationId', newConversationId);
          setTimeout(() => {
            if (this.$route.name === 'Home' && this.$route.query && newConversationId) {
              this.$router.push('/home?conversationId=' + newConversationId);
            } else {
              if (this.$route.name !== 'Home') {
                this.$router.push('/home');
              }
            }
            location.reload();
          }, 200);
        })
        .catch(() => {
          setTimeout(() => {
            if (this.$route.name !== 'Home') {
              this.$router.push('/home');
            }
            location.reload();
          }, 200);
        });
    } else {
      setTimeout(() => {
        if (this.$route.name !== 'Home') {
          this.$router.push('/home');
        }
        location.reload();
      }, 200);
    }
  }
}
